import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useSelector } from "react-redux"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { marketplaceApi } from "../infrastructure/marketplace.api"
import type { PluginInstallDto } from "../dtos/marketplace.dto"
import { PluginInstallsContext } from "./plugin-installs-context"

// Shared source of truth for the org's plugin installs. Lives above both the
// sidebar and the page content, so installing/uninstalling on the marketplace
// updates the nav (and any other consumer) without a manual refresh.
export function PluginInstallsProvider({ children }: { children: ReactNode }) {
  const organizationId = useSelector(selectCurrentOrganizationId)
  const [installs, setInstalls] = useState<PluginInstallDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const organizationIdRef = useRef(organizationId)
  organizationIdRef.current = organizationId

  const refresh = useCallback(async () => {
    const requestedOrganizationId = organizationId
    if (!requestedOrganizationId) {
      setInstalls([])
      return
    }
    setIsLoading(true)
    try {
      const data = await marketplaceApi.listInstalls(requestedOrganizationId)
      if (requestedOrganizationId === organizationIdRef.current) setInstalls(data)
    } catch {
      if (requestedOrganizationId === organizationIdRef.current) setInstalls([])
    } finally {
      if (requestedOrganizationId === organizationIdRef.current) {
        setIsLoading(false)
        setLoadedFor(requestedOrganizationId)
      }
    }
  }, [organizationId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const activeFamilies = useMemo(
    () => [
      ...new Set(
        installs
          .filter((i) => i.status === "Active" && i.familyId)
          .map((i) => i.familyId as string),
      ),
    ],
    [installs],
  )

  const hasLoaded = !!organizationId && loadedFor === organizationId

  const value = useMemo(
    () => ({ installs, activeFamilies, isLoading, hasLoaded, refresh }),
    [installs, activeFamilies, isLoading, hasLoaded, refresh],
  )

  return (
    <PluginInstallsContext.Provider value={value}>
      {children}
    </PluginInstallsContext.Provider>
  )
}
